// Single CSVbox entry point for the launch wizard. The server decides when to open the modal and
// with what config by pushing "csvbox-open"; parsed rows are pushed back to the event name the
// server chose, so a single hook can serve any importer.
//
// `@csvbox/csvboxjs` is code-split into its own chunk. We warm it on mount (fire-and-forget) so the
// chunk downloads as soon as an importer renders, not on every GT page, making the first open instant.
// The open handler re-imports it: native dynamic import returns the cached module on success and
// retries the fetch on failure, so a transient prewarm error never sticks.
//
// Payload of "csvbox-open":
//   - target: the importer element id this open is for. `handleEvent` is page-global, so several
//     importers on one page (e.g. test cases + known issues) all receive every push; each ignores
//     opens not addressed to its own element so a single push opens exactly one modal.
//   - license_key: CSVbox sheet license (differs per importer; server picks it)
//   - user_id: identifies the uploader to CSVbox
//   - upload_url: optional pre-resolved CSV to seed the modal (omit for a blank upload)
//   - reply_event: the event rows are pushed back on, as `{rows}` on success or `{error: true}` on
//     failure. CSVbox reports failures with no detail, so the server can only flash a generic message.
//     User-initiated close fires CSVbox's own onClose, not this callback, so it never reaches here.
//   - opened_event: the event pushed when the modal is actually up (CSVbox onReady), so the server
//     can drop its "Opening…" state.
// Matches the app's theme resolution: explicit `data-theme` on <html> wins, system preference
// is the fallback when no attribute is set.
function currentTheme() {
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr) return attr === "dark" ? "dark" : "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export default {
  mounted() {
    import("@csvbox/csvboxjs").catch(() => {});

    this.handleEvent("csvbox-open", async ({ target, license_key, user_id, upload_url, reply_event, opened_event }) => {
      if (target && target !== this.el.id) return;

      try {
        const { default: CSVboxImporter } = await import("@csvbox/csvboxjs");

        const importer = new CSVboxImporter(
          license_key,
          {},
          (success, data) => {
            if (success && data && Array.isArray(data.rows)) {
              this.pushEvent(reply_event, { rows: data.rows });
            } else {
              this.pushEvent(reply_event, { error: true });
            }
          },
          { lazy: false, theme: currentTheme() },
        );

        importer.setUser({ user_id: user_id || "anonymous" });
        if (upload_url) importer.setOptions({ upload_file_url: upload_url });

        importer.listen("onReady", () => this.pushEvent(opened_event, { target: this.el.id }));
        importer.openModal();
      } catch (_error) {
        this.pushEvent(reply_event, { error: true });
      }
    });
  },
};
